import { tt } from '@agos/shared'

/**
 * Which categories a recipe may be filed under - FRONTEND.md §9.349, the owner's C8.
 *
 * *"use categories for recepty from Kategórie… when I use Pridať recept, there are also wierd
 * categories, for example «Koktaily»?"*
 *
 * **Measured before it was designed.** The form offered ten hardcoded names, **four of which are not
 * categories at all** - `Boxy`, `Alkohol`, `Koktaily`, `Káva` - while omitting **twelve that are**,
 * including `Burgre` and `Šaláty`, which is what 14 of the owner's recipes should have been filed
 * under. And 57 of their 93 recipes carry a value their category list has never contained.
 *
 * **The stored value is always offered, even when it is not a category.** Otherwise a recipe filed
 * under `Iné` would silently change category the first time somebody opened it and pressed save,
 * which is how a data problem becomes a worse one. §10A.137's migration renames the four that have a
 * category to rename to; `Iné` deliberately stays, so this has to keep working for it indefinitely.
 */

export interface CategoryChoice {
  value: string
  label: string
}

/** Every active category, by name - the names a recipe's `category` column actually holds. */
export function categoryNames(payload: unknown): string[] {
  if (!Array.isArray(payload)) return []
  return payload.flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) return []
    const row = entry as Record<string, unknown>
    // `active === false` is switched off; a row with no flag at all is on, which is how the rest of
    // this module reads it.
    if (row.active === false) return []
    return typeof row.name === 'string' && row.name.trim() !== '' ? [row.name] : []
  })
}

/**
 * The options for the form's select: the franchise's categories, plus whatever this recipe says.
 *
 * The stored value comes **first** when it is not a category, because it is the answer the select
 * has to show as selected - a `<select>` whose value is absent from its options renders as blank and
 * then saves that blank over a category somebody chose deliberately.
 */
export function recipeCategoryOptions(
  payload: unknown,
  storedCategory: string | null | undefined,
): CategoryChoice[] {
  const names = categoryNames(payload)
  const options: CategoryChoice[] = []
  const stored = (storedCategory ?? '').trim()
  if (stored !== '' && !names.includes(stored)) {
    options.push({ value: stored, label: `${stored} - ${tt('nie je v kategóriách')}` })
  }
  for (const name of names) {
    options.push({ value: name, label: name })
  }
  return options
}

/**
 * The filter's options: the franchise's categories **and** every value in use.
 *
 * Both, because either alone is wrong. The categories alone hide the 57 recipes filed under something
 * else; the values in use alone are what the screen did before - *"I see only some of them"*, seven
 * names where the owner knows eighteen. A category with no recipes yet is worth offering too: an empty
 * result is an answer, and its absence from the list reads as the category not existing.
 */
export function recipeFilterOptions(
  payload: unknown,
  storedCategories: readonly (string | null | undefined)[],
): CategoryChoice[] {
  const names = categoryNames(payload)
  const inUse = [...new Set(storedCategories.map((value) => (value ?? '').trim()))].filter(
    (value) => value !== '' && !names.includes(value),
  )
  return [
    { value: 'all', label: tt('Všetky kategórie') },
    ...names.map((name) => ({ value: name, label: name })),
    // Named as what they are, so the 57 are findable *and* the reason they look odd is on the screen
    // rather than in a migration note.
    ...inUse.sort().map((name) => ({ value: name, label: `${name} - ${tt('nie je v kategóriách')}` })),
  ]
}
